/**
 * Creates a filters API backed by the legacy wsClient.
 * This is the adapter for the existing cloud web-service.
 *
 * Cloud endpoints:
 *   GET  /system/shop/:domain/customization/search/facet
 *   POST /system/shop/:domain/customization/search/facet
 *
 * Cloud response shape (facets.list):
 *   Array of facet objects or { facets: [...] }
 *   Per facet: { id, title, type, count, frequency, order, orderNatural, sortingMode, ignore }
 *
 * Admin facet model:
 *   { id, title, type, visible, searchable, orderNatural, order, sortingMode, source }
 *
 * Contract translation (cloud → admin):
 *   ignore → visible (inverted: ignore=true means visible=false)
 *   count/frequency are preserved when present from cloud
 *   searchable is always true in cloud (no dedicated field)
 */
export function createLegacyFiltersApi() {
  let _wsClient = null;

  function getClient() {
    if (!_wsClient) {
      // eslint-disable-next-line global-require
      const { wsClient } = require('util/appUtils');
      _wsClient = wsClient;
    }
    return _wsClient;
  }

  /**
   * Normalize the cloud response into a stable array of facets.
   * Cloud may return a bare array or { facets: [...] }.
   */
  function extractFacets(response) {
    if (Array.isArray(response)) return response;
    if (response && Array.isArray(response.facets)) return response.facets;
    return [];
  }

  /**
   * Map a cloud facet to the admin model.
   */
  function cloudToAdmin(raw) {
    return {
      id:           raw.id,
      title:        raw.title,
      type:         raw.type || 'string',
      visible:      raw.ignore !== undefined ? !raw.ignore : true,
      searchable:   true,
      orderNatural: raw.orderNatural !== undefined ? Boolean(raw.orderNatural) : true,
      order:        raw.order != null ? Number(raw.order) : null,
      sortingMode:  raw.sortingMode || null,
      source:       null, // cloud does not expose a source tag
      count:        raw.count != null ? Number(raw.count) : undefined,
      frequency:    raw.frequency != null ? Number(raw.frequency) : undefined,
    };
  }

  /**
   * Map admin facets back to the cloud save payload.
   * Cloud expects: { facets: [{ id, title, type, orderNatural, order, sortingMode, ignore }] }
   */
  function adminToCloud(facet) {
    const out = {
      id:           facet.id,
      title:        facet.title,
      type:         facet.type || 'string',
      orderNatural: Boolean(facet.orderNatural),
      order:        facet.order != null ? Number(facet.order) : undefined,
      sortingMode:  facet.sortingMode || undefined,
      ignore:       facet.visible === false,
    };
    // Remove undefined keys
    Object.keys(out).forEach(k => out[k] === undefined && delete out[k]);
    return out;
  }

  return {
    /**
     * @param {string} shopId
     * @returns {Promise<{ facets: Array }>}
     */
    async load(shopId) {
      const response = await getClient().getSearchFacets(shopId);
      const facets = extractFacets(response).map(cloudToAdmin);
      return { facets };
    },

    /**
     * @param {string} shopId
     * @param {Array} facets — admin model facets
     * @returns {Promise<{ facets: Array }>}
     */
    async save(shopId, facets) {
      const cloudFacets = facets.map(adminToCloud);
      const response = await getClient().updateSearchFacets(shopId, cloudFacets);
      const saved = extractFacets(response).map(cloudToAdmin);
      // If POST returns empty, re-fetch
      if (saved.length === 0) {
        return this.load(shopId);
      }
      return { facets: saved };
    },
  };
}
